import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export default function Page() {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = location.state?.userInfo || null;

  const itemIcons = {
    carton_entree: "📦",
    carton_salon: "📦",
    carton_cuisine: "📦",
    carton_chambre: "📦",
    carton_sam: "📦",
    carton_sdb: "📦",

    chaise: "🪑",
    frigo: "🧊",
    canape: "🛋️",
    lit: "🛏️",
    table: "🍽️",
    armoire: "🚪",
    commode: "🗄️",
    buffet: "🪑",
    four: "🔥",
    microOnde: "📡",
    douche: "🚿",
    baignoire: "🛁",
    lavabo: "🚰",
    bibliotheque: "📚",
    tableBasse: "🪑",
    miroir: "🪞",
    porteManteau: "🧥",
    banc: "🪑",
    cadre: "🖼️",
    etagere: "📚",
    console: "🗄️",
    meuble: "👞",

    machineALaver: "🧺",
    laveVaisselle: "🍽️",
    secheLinge: "🔥",
    congelateur: "❄️",

    wc: "🚽",
    meubleWC: "🗄️",
  };

  const furnitureByRoom = {
    Entrée: [
      { key: "banc", label: "Banc" },
      { key: "cadre", label: "Cadre" },
      { key: "carton_entree", label: "Carton" },
      { key: "console", label: "Console" },
      { key: "etagere", label: "Étagère murale" },
      { key: "meuble", label: "Meuble à chaussures" },
      { key: "miroir", label: "Miroir" },
      { key: "porteManteau", label: "Porte manteau" },
    ],

    "Salle de bain": [
      { key: "lavabo", label: "Lavabo" },
      { key: "wc", label: "WC (toilettes)" },
      { key: "meubleWC", label: "Meuble WC" },
      { key: "carton_sdb", label: "Carton" },
      { key: "douche", label: "Douche" },
      { key: "baignoire", label: "Baignoire" },
      { key: "machineALaver", label: "Machine à laver" },
      { key: "secheLinge", label: "Sèche-linge" },
    ],

    Salon: [
      { key: "canape", label: "Canapé" },
      { key: "carton_salon", label: "Carton" },
      { key: "tableBasse", label: "Table basse" },
      { key: "bibliotheque", label: "Bibliothèque" },
    ],

    Cuisine: [
      { key: "frigo", label: "Frigo" },
      { key: "congelateur", label: "Congélateur" },
      { key: "laveVaisselle", label: "Lave-vaisselle" },
      { key: "carton_cuisine", label: "Carton" },
      { key: "four", label: "Four" },
      { key: "microOnde", label: "Micro-ondes" },
    ],

    "Salle à manger": [
      { key: "table", label: "Table" },
      { key: "carton_sam", label: "Carton" },
      { key: "chaise", label: "Chaise" },
      { key: "buffet", label: "Buffet" },
    ],

    Chambre: [
      { key: "lit", label: "Lit" },
      { key: "carton_chambre", label: "Carton" },
      { key: "armoire", label: "Armoire" },
      { key: "commode", label: "Commode" },
    ],
  };

  const [items, setItems] = useState({});
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleIncrement = (key) => {
    if (isSubmitting) return;
    setItems((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));
  };

  const handleDecrement = (key) => {
    if (isSubmitting) return;
    setItems((p) => ({ ...p, [key]: Math.max((p[key] || 0) - 1, 0) }));
  };

  const getSummaryByRoom = () => {
    const result = [];
    Object.entries(furnitureByRoom).forEach(([room, itemsList]) => {
      const selectedItems = itemsList
        .map((item) => ({
          ...item,
          count: items[item.key] || 0,
        }))
        .filter((item) => item.count > 0);

      if (selectedItems.length === 0) return;
      result.push({ room, selectedItems });
    });
    return result;
  };

  const summary = getSummaryByRoom();

  const estimateVolumeM3 = () => {
    let total = 0;
    Object.values(items).forEach((count) => {
      total += count || 0;
    });
    return total * 0.5;
  };

  const getVolumeCode = (volume) => {
    if (volume <= 10) return "1-10";
    if (volume <= 20) return "11-20";
    if (volume <= 30) return "21-30";
    if (volume <= 40) return "31-40";
    if (volume <= 50) return "50";
    if (volume <= 60) return "60";
    if (volume <= 70) return "70";
    return ">71";
  };

  const toBooleanOrNull = (v) => {
    if (v === "oui") return true;
    if (v === "non") return false;
    return null;
  };

  const toTypeLogementBack = (v) => {
    if (!v) return "";
    return v.toUpperCase();
  };

  const toIntOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const volumeM3 = estimateVolumeM3();
    const volumeCode = getVolumeCode(volumeM3);

    const payloadBack = {
      nom: userInfo?.nom || "",
      prenom: userInfo?.prenom || "",
      adresseDepart: userInfo?.adresseDepart || "",
      adresseArrivee: userInfo?.adresseArrivee || "",
      dateDepart: userInfo?.dateDepart || "",
      email: userInfo?.email || "",
      telephone: userInfo?.telephone || "",
      distance: userInfo?.distance || "",
      dureeTrajet: userInfo?.dureeTrajet || "",
      volumeCode,
      volumeEstimeM3: volumeM3,

      typeLogementDepart: toTypeLogementBack(userInfo?.logementDepart || ""),
      ascenseurDepart: toBooleanOrNull(userInfo?.ascenseurDepart),
      etageDepart: toIntOrNull(userInfo?.etageDepart),

      typeLogementArrivee: toTypeLogementBack(userInfo?.logementArrivee || ""),
      ascenseurArrivee: toBooleanOrNull(userInfo?.ascenseurArrivee),
      etageArrivee: toIntOrNull(userInfo?.etageArrivee),

      meubles: summary.map(({ room, selectedItems }) => ({
        room,
        items: selectedItems.map(({ key, label, count }) => ({
          key,
          label,
          count,
        })),
      })),
    };

    console.log("Payload envoyé au backend :", payloadBack);

    try {
      const response = await fetch("http://demenagerfact.com/api/devis/confirmer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadBack),
      });

      const data = await response.json();
      console.log("Status backend :", response.status);
      console.log("Réponse backend :", data);

      if (!response.ok) {
        alert(data.message || "Une erreur est survenue lors de l'envoi du devis.");
        setIsSubmitting(false);
        return;
      }

      navigate("/confirmation");
    } catch (error) {
      console.error("Erreur fetch :", error);
      alert("Impossible de contacter le serveur backend.");
      setIsSubmitting(false);
    }
  };

  const formatOuiNon = (v) => {
    if (v === "oui") return "Oui";
    if (v === "non") return "Non";
    return "";
  };

  return (
    <div className="relative min-h-screen bg-gray-100 overflow-x-hidden">
      {isSubmitting && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex flex-col items-center justify-center px-4">
          <div className="w-14 h-14 sm:w-16 sm:h-16 border-4 border-white border-t-transparent rounded-full animate-spin mb-6"></div>
          <p className="text-white text-lg sm:text-xl font-semibold text-center">
            Envoi du devis en cours...
          </p>
          <p className="text-white/80 text-sm mt-2 text-center">
            Merci de patienter
          </p>
        </div>
      )}

      <nav className="bg-blue-600 text-white px-4 sm:px-6 py-4 relative z-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div className="flex justify-center lg:justify-start items-center">
            <img
              src="/Logo.svg"
              alt="Logo"
              className="h-14 sm:h-20 lg:h-24 w-auto max-w-[220px] sm:max-w-[300px] lg:max-w-[380px] object-contain block"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 lg:gap-6">
            <button
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => navigate("/")}
            >
              Accueil
            </button>

            <button
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => navigate("/devis")}
            >
              Devis
            </button>

            <button
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Avis
            </button>
          </div>

          <div className="flex items-center justify-center lg:justify-end">
            <div className="flex flex-col items-center">
              <button
                disabled={isSubmitting}
                className="bg-red-500 px-4 py-2 rounded-md font-bold text-white text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                07 66 11 91 93
              </button>
              <span className="mt-1 text-xs sm:text-sm">Assistant client</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="min-h-screen bg-gray-100 px-4 sm:px-6 lg:px-8 pb-24 relative">
        <div className="max-w-7xl mx-auto">
          <input
            disabled={isSubmitting}
            className="w-full p-3 text-sm sm:text-base rounded-md border mb-6 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {Object.entries(furnitureByRoom).map(([room, list]) => {
            const filtered = list.filter((i) =>
              i.label.toLowerCase().includes(search.toLowerCase())
            );
            if (!filtered.length) return null;

            return (
              <div key={room} className="mb-10">
                <h2 className="text-xl sm:text-2xl font-bold mb-4">{room}</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                  {filtered.map((item) => (
                    <div
                      key={item.key}
                      className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col items-center"
                    >
                      <div className="text-4xl sm:text-5xl mb-2">
                        {itemIcons[item.key] || "📦"}
                      </div>

                      <div className="font-semibold mb-3 text-center text-sm sm:text-base min-h-[40px] flex items-center">
                        {item.label}
                      </div>

                      <div className="flex items-center space-x-4">
                        <button
                          disabled={isSubmitting}
                          onClick={() => handleDecrement(item.key)}
                          className="bg-gray-200 rounded-full w-8 h-8 text-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          -
                        </button>
                        <span className="font-bold text-lg min-w-[24px] text-center">
                          {items[item.key] || 0}
                        </span>
                        <button
                          disabled={isSubmitting}
                          onClick={() => handleIncrement(item.key)}
                          className="bg-gray-200 rounded-full w-8 h-8 text-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          disabled={isSubmitting}
          onClick={() => setIsModalOpen(true)}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 bg-blue-600 text-white p-3 sm:p-4 rounded-full text-3xl sm:text-5xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ➡️
        </button>

        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-3 sm:px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-4 sm:p-6 max-h-[85vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center">
                Confirmer votre devis ?
              </h2>

              {userInfo && (
                <div className="mb-4 border-b pb-3">
                  <h3 className="font-semibold text-base sm:text-lg mb-2">
                    Vos informations
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1 break-words">
                    <li>
                      <span className="font-medium">Nom :</span> {userInfo.nom}
                    </li>
                    <li>
                      <span className="font-medium">Prénom :</span> {userInfo.prenom}
                    </li>
                    <li>
                      <span className="font-medium">Email :</span> {userInfo.email}
                    </li>
                    <li>
                      <span className="font-medium">Téléphone :</span> {userInfo.telephone}
                    </li>
                    <li>
                      <span className="font-medium">Départ :</span> {userInfo.adresseDepart}
                    </li>
                    <li>
                      <span className="font-medium">Arrivée :</span> {userInfo.adresseArrivee}
                    </li>
                    <li>
                      <span className="font-medium">Date :</span> {userInfo.dateDepart}
                    </li>

                    {userInfo.logementDepart && (
                      <li>
                        <span className="font-medium">Logement départ :</span>{" "}
                        {userInfo.logementDepart}
                        {userInfo.logementDepart === "appartement" && userInfo.ascenseurDepart && (
                          <>
                            {" "}— <span className="font-medium">Ascenseur :</span>{" "}
                            {formatOuiNon(userInfo.ascenseurDepart)}
                          </>
                        )}
                        {userInfo.logementDepart === "appartement" &&
                          userInfo.ascenseurDepart === "non" &&
                          userInfo.etageDepart !== "" && (
                            <>
                              {" "}— <span className="font-medium">Étage :</span>{" "}
                              {userInfo.etageDepart}
                            </>
                          )}
                      </li>
                    )}

                    {userInfo.logementArrivee && (
                      <li>
                        <span className="font-medium">Logement arrivée :</span>{" "}
                        {userInfo.logementArrivee}
                        {userInfo.logementArrivee === "appartement" && userInfo.ascenseurArrivee && (
                          <>
                            {" "}— <span className="font-medium">Ascenseur :</span>{" "}
                            {formatOuiNon(userInfo.ascenseurArrivee)}
                          </>
                        )}
                        {userInfo.logementArrivee === "appartement" &&
                          userInfo.ascenseurArrivee === "non" &&
                          userInfo.etageArrivee !== "" && (
                            <>
                              {" "}— <span className="font-medium">Étage :</span>{" "}
                              {userInfo.etageArrivee}
                            </>
                          )}
                      </li>
                    )}

                    {userInfo.distance && (
                      <li>
                        <span className="font-medium">Distance :</span> {userInfo.distance}
                      </li>
                    )}
                    {userInfo.dureeTrajet && (
                      <li>
                        <span className="font-medium">Durée :</span> {userInfo.dureeTrajet}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {summary.length === 0 ? (
                <p className="text-center text-gray-600 mb-4 text-sm sm:text-base">
                  Vous n&apos;avez sélectionné aucun élément.
                </p>
              ) : (
                <div className="space-y-4">
                  {summary.map(({ room, selectedItems }) => (
                    <div key={room} className="border-b pb-3 last:border-b-0">
                      <h3 className="font-semibold text-base sm:text-lg mb-1">{room}</h3>
                      <ul className="list-disc list-inside text-sm text-gray-700">
                        {selectedItems.map((it) => (
                          <li key={it.key}>
                            {it.count}x {it.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  disabled={isSubmitting}
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>

                <button
                  disabled={isSubmitting}
                  onClick={handleConfirm}
                  className="w-full sm:w-auto px-4 py-2 rounded-md bg-blue-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Envoi..." : "Confirmer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}